import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Logo from "@/components/Logo";
import { requestPasswordReset, formatApiError } from "@/lib/api";

/**
 * Public "I forgot my password" page.
 *
 * Submits the email to /api/auth/forgot-password. The backend always
 * responds with a generic 200 (so this page can't be used to enumerate
 * registered emails) — we mirror that on the UI by always showing a
 * "check your inbox" confirmation, no matter whether the email exists.
 */
export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!email.trim() || submitting) return;
        setSubmitting(true);
        try {
            await requestPasswordReset(email.trim());
            setDone(true);
        } catch (err) {
            // Even on backend errors we still show the same confirmation
            // — leaking timing differences is the same vulnerability we
            // were avoiding on the success path.
            // eslint-disable-next-line no-console
            console.warn("forgot-password:", formatApiError(err));
            setDone(true);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-6 py-16 paper-texture" data-testid="forgot-page">
            <div className="w-full max-w-md">
                <div className="flex items-center gap-3 mb-8 justify-center">
                    <Logo variant="icon" size={56} />
                    <div>
                        <div className="font-serif-display text-3xl text-[var(--vm-text)] leading-none lowercase tracking-tight">rooteed</div>
                        <div className="text-xs uppercase tracking-[0.2em] text-[var(--vm-sage)] mt-1">Garden Journal</div>
                    </div>
                </div>

                <div className="journal-card p-8">
                    {done ? (
                        <div data-testid="forgot-confirmation" className="text-center">
                            <div className="w-12 h-12 rounded-full bg-[var(--vm-sage)]/15 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle2 className="w-6 h-6 text-[var(--vm-sage)]" strokeWidth={1.6} />
                            </div>
                            <h1 className="font-serif-display text-2xl text-[var(--vm-text)] mb-2">
                                Check your inbox
                            </h1>
                            <p className="text-sm text-[var(--vm-text-secondary)] leading-relaxed">
                                If <strong className="text-[var(--vm-text)]">{email}</strong> is registered,
                                a reset link is on its way. The link expires in 30 minutes — open it from
                                the same device to be safe.
                            </p>
                            <p className="text-xs text-[var(--vm-text-muted)] mt-4">
                                Didn't get anything? Check spam, or try again with a different email.
                            </p>
                            <div className="mt-6 flex flex-col gap-2">
                                <Link to="/login">
                                    <Button data-testid="forgot-back-to-login"
                                        className="w-full rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]">
                                        Back to sign in
                                    </Button>
                                </Link>
                                <button
                                    type="button"
                                    data-testid="forgot-try-again"
                                    onClick={() => { setDone(false); setEmail(""); }}
                                    className="text-xs text-[var(--vm-text-muted)] hover:text-[var(--vm-text)] underline"
                                >
                                    Try a different email
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <Link to="/login" className="text-xs text-[var(--vm-text-muted)] hover:text-[var(--vm-text)] inline-flex items-center mb-4">
                                <ArrowLeft className="w-3 h-3 mr-1" /> Back to sign in
                            </Link>
                            <h1 className="font-serif-display text-3xl text-[var(--vm-text)] mb-2">
                                Forgot password?
                            </h1>
                            <p className="text-sm text-[var(--vm-text-muted)] mb-6">
                                Pop in the email you signed up with and we'll send you a reset link.
                            </p>
                            <form onSubmit={submit} className="space-y-4">
                                <div>
                                    <Label htmlFor="forgot-email" className="text-xs uppercase tracking-wide text-[var(--vm-sage)]">
                                        Email
                                    </Label>
                                    <div className="relative mt-1">
                                        <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--vm-text-muted)]" />
                                        <Input
                                            id="forgot-email"
                                            type="email"
                                            data-testid="forgot-email-input"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="you@example.com"
                                            required
                                            autoFocus
                                            className="pl-9 bg-[var(--vm-bg)] border-[var(--vm-border)]"
                                        />
                                    </div>
                                </div>
                                <Button
                                    type="submit"
                                    disabled={submitting || !email.trim()}
                                    data-testid="forgot-submit-btn"
                                    className="w-full rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]"
                                >
                                    {submitting
                                        ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>)
                                        : "Email me a reset link"}
                                </Button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
